/**
 * CFS header behaviour (build spec §4.2).
 *
 * - Compact sticky bar: shown once the full header has scrolled out of view (IntersectionObserver
 *   on a sentinel; no scroll handlers). Sets [data-cfs-condensed] on <html>; the sticky elements
 *   that sit under the bar take their offset from it (--header-height, scoped in
 *   assets/cfs-global.css, header part; --cfs-compact-offset holds the measured bar height). A new
 *   sticky element under the bar must be added to that list, or it reads 0px.
 *   On the homepage for guests on phones the hero has the search box ([data-cfs-compact-trigger])
 *   and the header leaves its own out, so the bar also waits until the hero search has scrolled
 *   under it: one search box on screen at a time.
 * - Search: clicking any search field ([data-cfs-search-input]: header, compact bar, homepage hero),
 *   typing a character or pressing ↓ opens Horizon's predictive search modal (#search-modal) and
 *   carries the text over (keys typed while it opens are buffered). Tabbing past the field does
 *   not open it. Without JS the form submits to /search.
 * - Postcode: "Delivering to" pill opens a small dialog; the postcode is kept in localStorage
 *   (`cfs:postcode`) for the basket's delivery widget (T3.1).
 */

const POSTCODE_KEY = 'cfs:postcode';
const UK_POSTCODE = /^([A-Z]{1,2}\d[A-Z\d]?)\s*(\d[A-Z]{2})$/i;

/** @param {string} value */
export function normalisePostcode(value) {
  const match = UK_POSTCODE.exec(value.trim());
  return match ? `${match[1]} ${match[2]}`.toUpperCase() : null;
}

export function getSavedPostcode() {
  try {
    return localStorage.getItem(POSTCODE_KEY);
  } catch {
    return null;
  }
}

class CfsHeader extends HTMLElement {
  /** @type {IntersectionObserver | undefined} */
  #observer;
  /** @type {IntersectionObserver | undefined} */
  #triggerObserver;
  /** @type {ResizeObserver | undefined} */
  #sizeObserver;
  #headerGone = false; // the full header has scrolled away
  #triggerGone = true; // the hero search (if any) has scrolled under the bar, or is hidden
  #wide = matchMedia('(min-width: 990px)');

  connectedCallback() {
    this.compact = /** @type {HTMLElement | null} */ (this.querySelector('[data-cfs-compact]'));
    const sentinel = this.querySelector('[data-cfs-header-sentinel]');

    if (this.compact && sentinel && 'IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver(([entry]) => {
        this.#headerGone = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.#setCondensed(this.#headerGone && this.#triggerGone);
      });
      this.#observer.observe(sentinel);
      this.#watchTrigger();
      this.#watchSize();
    }

    document.addEventListener('click', this.#onSearchClick);
    document.addEventListener('keydown', this.#onSearchKeydown);

    this.#setupPostcode();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#triggerObserver?.disconnect();
    this.#sizeObserver?.disconnect();
    this.#wide.removeEventListener('change', this.#rewatchTrigger);
    document.removeEventListener('click', this.#onSearchClick);
    document.removeEventListener('keydown', this.#onSearchKeydown);
    // A re-rendered header (theme editor) starts again from the default offset, not stale state.
    document.documentElement.removeAttribute('data-cfs-condensed');
    document.documentElement.style.removeProperty('--cfs-compact-offset');
  }

  /**
   * Publishes the compact bar's height as --cfs-compact-offset on <html> for the sticky offsets.
   * The bar is laid out while hidden, so it can be measured at any time. Only a height other than
   * the 64px CSS default is written, so an ordinary load writes nothing and restyles nothing.
   */
  #watchSize() {
    if (!this.compact || !('ResizeObserver' in window)) return;
    let published = 64;
    this.#sizeObserver = new ResizeObserver(() => {
      const height = this.compact?.offsetHeight;
      if (!height || height === published) return;
      published = height;
      document.documentElement.style.setProperty('--cfs-compact-offset', `${height}px`);
    });
    this.#sizeObserver.observe(this.compact);
  }

  #watchTrigger() {
    const trigger = document.querySelector('[data-cfs-compact-trigger]');
    if (!trigger || !this.compact) return;
    const offset = this.compact.offsetHeight || 64;
    this.#triggerObserver = new IntersectionObserver(
      ([entry]) => {
        const hidden = entry.target.getClientRects().length === 0; // desktop: search is in the header
        this.#triggerGone = hidden || (!entry.isIntersecting && entry.boundingClientRect.top < offset);
        this.#setCondensed(this.#headerGone && this.#triggerGone);
      },
      { rootMargin: `-${offset}px 0px 0px 0px` }
    );
    this.#triggerObserver.observe(trigger);
    this.#wide.addEventListener('change', this.#rewatchTrigger);
  }

  /** The hero search only shows below 990px; observing again reports its current state. */
  #rewatchTrigger = () => {
    const trigger = document.querySelector('[data-cfs-compact-trigger]');
    if (!trigger || !this.#triggerObserver) return;
    this.#triggerObserver.unobserve(trigger);
    this.#triggerObserver.observe(trigger);
  };

  /** @param {boolean} condensed */
  #setCondensed(condensed) {
    if (!this.compact) return;
    this.classList.toggle('cfs-header--condensed', condensed);
    this.compact.toggleAttribute('inert', !condensed);
    this.compact.setAttribute('aria-hidden', String(!condensed));
    // CFS: one attribute on <html> instead of --header-height on <body>. An inherited property
    // changed on <body> restyles every element (60-100 ms at 4x CPU) and the offsetHeight read
    // forced a layout, both on each condense and expand while scrolling.
    document.documentElement.toggleAttribute('data-cfs-condensed', condensed);
  }

  /** @param {Event} event */
  #searchInput(event) {
    const target = /** @type {HTMLElement | null} */ (event.target);
    return /** @type {HTMLInputElement | null} */ (target?.closest?.('input[data-cfs-search-input]') ?? null);
  }

  /** @param {MouseEvent} event */
  #onSearchClick = (event) => {
    const input = this.#searchInput(event);
    if (input) this.#openPredictiveSearch(input, input.value);
  };

  /** @param {KeyboardEvent} event */
  #onSearchKeydown = (event) => {
    const input = this.#searchInput(event);
    if (!input || event.ctrlKey || event.metaKey || event.altKey || event.isComposing) return;
    if (event.key.length === 1) {
      event.preventDefault();
      this.#openPredictiveSearch(input, input.value + event.key);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.#openPredictiveSearch(input, input.value);
    }
  };

  /**
   * Opens the predictive search modal synchronously (so focus moves inside the same tap or
   * keypress: iOS only shows the keyboard for focus within the gesture) and runs the search.
   * Horizon's DialogOpenEvent is not fired: its handler resets the search ~100 ms later, which
   * would clear the carried-over text. Esc and outside clicks are handled by the modal's markup.
   * @param {HTMLInputElement} source
   * @param {string} text
   */
  #openPredictiveSearch(source, text) {
    const modal = /** @type {any} */ (document.getElementById('search-modal'));
    const dialog = /** @type {HTMLDialogElement | null} */ (modal?.querySelector('dialog'));
    const modalInput = /** @type {HTMLInputElement | null} */ (modal?.querySelector('input[type="search"]'));
    if (!dialog || !modalInput) return; // no modal: the form submits normally

    // Desktop: open the panel exactly over the header search bar, so it reads as the bar
    // dropping down rather than a separate box (owner review).
    const bar = source.closest('.cfs-search');
    const anchor = () => {
      const anchored = Boolean(bar) && matchMedia('(min-width: 990px)').matches;
      dialog.classList.toggle('cfs-search-anchored', anchored);
      if (!anchored || !bar) return;
      const rect = bar.getBoundingClientRect();
      dialog.style.setProperty('--cfs-search-top', `${Math.round(rect.top)}px`);
      dialog.style.setProperty('--cfs-search-left', `${Math.round(rect.left)}px`);
      dialog.style.setProperty('--cfs-search-width', `${Math.round(rect.width)}px`);
    };
    anchor();
    if (!dialog.dataset.cfsAnchored) {
      dialog.dataset.cfsAnchored = 'true';
      window.addEventListener('resize', () => dialog.open && anchor());
    }
    if (source.placeholder) modalInput.placeholder = source.placeholder;

    if (!dialog.open) dialog.showModal();
    source.value = '';
    modalInput.focus();
    modalInput.value = text;
    modalInput.setSelectionRange(text.length, text.length);
    if (text.trim()) modalInput.dispatchEvent(new InputEvent('input', { bubbles: true, inputType: 'insertText', data: text }));
  }

  #setupPostcode() {
    const dialog = /** @type {HTMLDialogElement | null} */ (this.querySelector('[data-cfs-postcode-dialog]'));
    if (!dialog) return;

    const form = /** @type {HTMLFormElement} */ (dialog.querySelector('[data-cfs-postcode-form]'));
    const input = /** @type {HTMLInputElement} */ (dialog.querySelector('[data-cfs-postcode-input]'));
    const error = /** @type {HTMLElement} */ (dialog.querySelector('[data-cfs-postcode-error]'));
    /** @type {HTMLElement | null} */
    let opener = null;

    const render = () => {
      const saved = getSavedPostcode();
      for (const label of document.querySelectorAll('[data-cfs-postcode-label]')) {
        label.textContent = saved ?? 'enter postcode';
      }
    };
    render();

    // Any [data-cfs-postcode-open] on the page (header pill, basket "Change") opens this dialog.
    document.addEventListener('click', (event) => {
      const button = /** @type {HTMLElement} */ (event.target).closest?.('[data-cfs-postcode-open]');
      if (!button) return;
      opener = /** @type {HTMLElement} */ (button);
      input.value = getSavedPostcode() ?? '';
      error.hidden = true;
      input.removeAttribute('aria-invalid');
      dialog.showModal();
      input.focus();
    });

    dialog.querySelector('[data-cfs-postcode-cancel]')?.addEventListener('click', () => dialog.close());
    dialog.addEventListener('close', () => opener?.focus());
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close(); // backdrop
    });

    form.addEventListener('submit', (event) => {
      const postcode = normalisePostcode(input.value);
      if (!postcode) {
        event.preventDefault();
        error.hidden = false;
        input.setAttribute('aria-invalid', 'true');
        input.focus();
        return;
      }
      try {
        localStorage.setItem(POSTCODE_KEY, postcode);
      } catch {
        // Private mode: the label still updates for this page.
      }
      for (const label of document.querySelectorAll('[data-cfs-postcode-label]')) label.textContent = postcode;
      document.dispatchEvent(new CustomEvent('cfs:postcode', { detail: { postcode } }));
    });
  }
}

if (!customElements.get('cfs-header')) customElements.define('cfs-header', CfsHeader);

// On desktop the page scrolls inside .page-wrapper (Horizon), so the window itself must stay at the
// top. A link to a spot on the page also scrolled the window: after a newsletter sign-up Shopify
// returns to /#CfsNewsletter and the page slid up over a white gap (owner, 6 Oct 2026). The page
// still scrolls to the spot inside .page-wrapper.
const wideScreen = matchMedia('(min-width: 990px)');
const keepWindowAtTop = () => {
  if (wideScreen.matches && window.scrollY !== 0) window.scrollTo(0, 0);
};
window.addEventListener('scroll', keepWindowAtTop, { passive: true });
keepWindowAtTop();
